import React, { useState, useMemo } from 'react';
import { toast } from "react-toastify";

const PromoCodes = ({ promoCodes = [], loading, stats, setShowPromoModal }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("code"); // code, value, usage
  const [sortOrder, setSortOrder] = useState("asc");

  const sortedAndFilteredPromoCodes = useMemo(() => {
    let filtered = promoCodes.filter((promo) => {
      const code = (promo.code || "").toLowerCase();
      const description = (promo.description || "").toLowerCase();
      const eventTitle = (promo.eventId?.title || "").toLowerCase();
      const status = (promo.status || "active").toLowerCase();
      
      const matchesSearch = 
        code.includes(searchTerm.toLowerCase()) ||
        description.includes(searchTerm.toLowerCase()) ||
        eventTitle.includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || status === statusFilter.toLowerCase();
      
      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === "code") {
        comparison = (a.code || "").localeCompare(b.code || "");
      } else if (sortBy === "value") {
        comparison = (a.amount || 0) - (b.amount || 0);
      } else if (sortBy === "usage") {
        comparison = (a.currentUses || 0) - (b.currentUses || 0);
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [promoCodes, searchTerm, statusFilter, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Helper to get a consistent color for promo icons
  const getPromoColor = (code) => {
    const colors = ["bg-primary", "bg-success", "bg-info", "bg-warning", "bg-danger"];
    let hash = 0;
    for (let i = 0; i < code.length; i++) {
      hash = code.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="promo-codes-section mt-4">
      {/* Promo Statistics Cards */}
      <div className="row g-4">
        <div className="col-12 col-md-4">
          <div className="event-stat-card">
            <div className="card-top">
              <div>
                <p className="card-label">Active Discounts</p>
                <h2 className="card-value">{stats.activeDiscounts}</h2>
              </div>
              <div className="card-icon-wrapper icon-bg-blue">
                <i className="isax isax-ticket-discount icon-text-blue" />
              </div>
            </div>
            <div className="card-footer">
              <p className="description">Running Now</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="event-stat-card">
            <div className="card-top">
              <div>
                <p className="card-label">Total Redeemed</p>
                <h2 className="card-value">{stats.totalRedeemed}</h2>
              </div>
              <div className="card-icon-wrapper icon-bg-orange">
                <i className="isax isax-people icon-text-orange" />
              </div>
            </div>
            <div className="card-footer">
              <p className="description">Cumulative uses</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="event-stat-card">
            <div className="card-top">
              <div>
                <p className="card-label">Revenue Impact</p>
                <h2 className="card-value">${stats.revenueImpact}</h2>
              </div>
              <div className="card-icon-wrapper icon-bg-blue">
                <i className="isax isax-money-send icon-text-blue" />
              </div>
            </div>
            <div className="card-footer">
              <p className="description">Discounts Given</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Table Card */}
      <div className="modern-card mt-5">
        {/* Filters Bar */}
        <div className="modern-filter-bar">
          <div className="d-flex flex-column flex-md-row gap-4">
            <div className="modern-search-wrapper w-100">
              <i className="isax isax-search-normal-1" />
              <input
                type="text"
                className="modern-search-input"
                placeholder="Search promo codes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <div className="modern-select-wrapper">
                <i className="isax isax-filter prefix-icon" />
                <select 
                  className="modern-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                </select>
                <i className="isax isax-arrow-down-1 suffix-icon" />
              </div>
              <div className="results-badge">
                {loading ? <span>Loading...</span> : (
                  <>
                    <span className="fw-bold text-dark me-1">{sortedAndFilteredPromoCodes.length}</span>
                    <span>codes</span>
                  </>
                )}
              </div>
              <button
                className="btn btn-primary-gradient btn-sm rounded-pill px-4"
                onClick={() => setShowPromoModal(true)}
              >
                <i className="isax isax-add-circle me-1"></i> Create Promo
              </button>
            </div>
          </div>
        </div>

        {/* Table Header */}
        <div className="modern-table-header flex-column flex-md-row align-items-start align-items-md-center">
          <h2 className="fw-bold mb-3 mb-md-0">Promo Codes ({sortedAndFilteredPromoCodes.length})</h2>
          <div className="d-flex flex-wrap align-items-center gap-3 gap-md-4">
            <span className="text-muted small">Sort by:</span>
            <div className="d-flex gap-2">
              <button 
                className={`sort-btn ${sortBy === 'code' ? 'active' : ''}`}
                onClick={() => handleSort('code')}
              >
                Code {sortBy === 'code' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button 
                className={`sort-btn ${sortBy === 'value' ? 'active' : ''}`}
                onClick={() => handleSort('value')}
              >
                Value {sortBy === 'value' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button 
                className={`sort-btn ${sortBy === 'usage' ? 'active' : ''}`}
                onClick={() => handleSort('usage')}
              >
                Usage {sortBy === 'usage' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-5 text-center">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : sortedAndFilteredPromoCodes.length > 0 ? (
          <div className="table-responsive">
            <table className="table modern-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Name / Code</th>
                  <th>Event</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th className="text-center">Usage</th>
                  <th className="text-center">Status</th>
                  <th>Expiry</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndFilteredPromoCodes.map((promo) => (
                  <tr key={promo._id || promo.id}>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <div className={`event-avatar ${getPromoColor(promo.code)} text-white`}>
                          <i className="isax isax-ticket-discount" />
                        </div>
                        <div>
                          <p className="fw-bold text-dark mb-0">
                            {promo.description || "Unnamed Promo"}
                          </p>
                          <div className="d-flex align-items-center gap-2 mt-1">
                            <span className="badge bg-light text-dark border extra-small px-2 py-1">
                              {promo.code}
                            </span>
                            <button
                              className="btn btn-link p-0 extra-small text-primary"
                              title="Copy Code"
                              onClick={() => {
                                navigator.clipboard.writeText(promo.code);
                                toast.info("Code copied!");
                              }}
                            >
                              <i className="isax isax-copy"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="small fw-medium text-dark">
                        {promo.eventId?.title || "All Events"}
                      </span>
                    </td>
                    <td>
                      <span className="small text-capitalize text-muted">
                        {promo.type}
                      </span>
                    </td>
                    <td>
                      <span className="fw-bold text-dark">
                        {promo.type === "percentage"
                          ? `${promo.amount}%`
                          : `$${promo.amount}`}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="usage-info mx-auto" style={{ maxWidth: "80px" }}>
                        <span className="extra-small fw-bold text-dark">
                          {promo.currentUses || 0} / {promo.maxUses || "∞"}
                        </span>
                        <div className="progress mt-1" style={{ height: "6px", backgroundColor: "#f1f5f9", borderRadius: "10px" }}>
                          <div
                            className="progress-bar bg-primary rounded-pill"
                            style={{
                              width: promo.maxUses
                                ? `${((promo.currentUses || 0) / promo.maxUses) * 100}%`
                                : "0%",
                            }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="text-center">
                      <span className={`status-badge ${(promo.status || "active").toLowerCase()}`}>
                        {promo.status || "Active"}
                      </span>
                    </td>
                    <td>
                      <span className="text-muted small">
                        {promo.expiresAt
                          ? new Date(promo.expiresAt).toLocaleDateString()
                          : "No Expiry"}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex justify-content-end gap-1">
                        <button className="modern-action-btn edit" title="Edit">
                          <i className="isax isax-edit"></i>
                        </button>
                        <button className="modern-action-btn delete" title="Delete">
                          <i className="isax isax-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-5 text-center bg-light bg-opacity-25">
             <div className="mx-auto bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-4" style={{ width: "80px", height: "80px" }}>
               <i className="isax isax-ticket-discount text-primary" style={{ fontSize: "40px" }}></i>
             </div>
             <h3 className="fw-bold text-dark mb-2">No Promo Codes Found</h3>
             <p className="text-muted mb-0">You haven't created any discounts for your events yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoCodes;
